import { createSelector } from 'reselect';
import StarTitle from '@atlassian/jira-common-directory-v2/src/components/star-title-cell/view.tsx';
import type { HeaderConfiguration } from '@atlassian/jira-directory-base/src/model/index.tsx';
import {
	EDIT,
	COPY,
	DELETE,
	CHANGE_OWNER,
} from '@atlassian/jira-directory-base/src/model/operation-types.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { NAME, POPULARITY, FAVOURITED, OWNER } from '../model/sort-field.tsx';
import { getAtlassianAccountId } from '../state/selectors/app/index.tsx';
import ActionsView from './content/actions/index.tsx';
import AccessCell from './content/cells/access/index.tsx';
import ActionsCell from './content/cells/actions/index.tsx';
import EditCell from './content/cells/edit/index.tsx';
import FavoriteCell from './content/cells/favourite-v2/index.tsx';
import OwnerCell from './content/cells/owner/index.tsx';
import PopularityCell from './content/cells/popularity/index.tsx';
import TitleCell from './content/cells/title/index.tsx';
import ChangeOwnerModal from './content/change-owner-modal/index.tsx';
import CopyModal from './content/copy-modal/index.tsx';
import DeleteModal from './content/delete-modal/index.tsx';
import EditModal from './content/edit-modal/index.tsx';
import FilterView from './content/filter/index.tsx';
import messages from './messages.tsx';
import PopularityWithActionsCell from './content/cells/popularity-with-actions/index.tsx';

export const operationModals = {
	[COPY]: componentWithFG('nin-save-filter-dialog-entry-point', () => null, CopyModal),
	[CHANGE_OWNER]: ChangeOwnerModal,
	[EDIT]: componentWithFG('nin-save-filter-dialog-entry-point', () => null, EditModal),
	[DELETE]: DeleteModal,
} as const;

export const emptyFilterResultConfiguration = {
	message: messages.emptyFilterResultMessage,
} as const;

export const getHeaderConfiguration = (): HeaderConfiguration<string> => ({
	ActionsView,
	FilterView,
	caption: messages.headerCaption,
});

export const getTableConfiguration = createSelector(getAtlassianAccountId, (atlassianAccountId) =>
	[
		atlassianAccountId !== undefined
			? {
					title: StarTitle,
					Cell: FavoriteCell,
					sortField: FAVOURITED,
					width: isVisualRefreshEnabled() ? 3 : 3.1,
					isIconOnlyHeader: isVisualRefreshEnabled(),
					ascendingSortTooltip: isVisualRefreshEnabled()
						? messages.starredToUnstarredOrder
						: undefined,
					descendingSortTooltip: isVisualRefreshEnabled()
						? messages.unstarredToStarredOrder
						: undefined,
				}
			: undefined,
		{
			title: messages.filter,
			Cell: TitleCell,
			sortField: NAME,
			// eslint-disable-next-line no-nested-ternary
			width: atlassianAccountId ? (isVisualRefreshEnabled() ? 23.6 : 22) : 25,
			ascendingSortTooltip: isVisualRefreshEnabled() ? messages.aToZOrder : undefined,
			descendingSortTooltip: isVisualRefreshEnabled() ? messages.zToAOrder : undefined,
		},
		atlassianAccountId !== undefined
			? {
					title: messages.owner,
					Cell: OwnerCell,
					sortField: OWNER,
					width: 20,
					ascendingSortTooltip: isVisualRefreshEnabled() ? messages.aToZOrder : undefined,
					descendingSortTooltip: isVisualRefreshEnabled() ? messages.zToAOrder : undefined,
				}
			: undefined,
		{
			title: messages.viewers,
			Cell: AccessCell,
			width: atlassianAccountId ? 20 : 30,
		},
		{
			title: messages.editors,
			Cell: EditCell,
			width: atlassianAccountId ? 20 : 30,
		},
		{
			title: messages.popularity,
			Cell: isVisualRefreshEnabled() ? PopularityWithActionsCell : PopularityCell,
			sortField: POPULARITY,
			// eslint-disable-next-line no-nested-ternary
			width: atlassianAccountId ? (isVisualRefreshEnabled() ? 13.4 : 10) : 15,
			ascendingSortTooltip: isVisualRefreshEnabled() ? messages.lowestToHighestOrder : undefined,
			descendingSortTooltip: isVisualRefreshEnabled() ? messages.highestToLowestOrder : undefined,
			dataTestId: isVisualRefreshEnabled()
				? 'filters-main.content.headerCell.actions.container'
				: undefined,
		},
		atlassianAccountId !== undefined && !isVisualRefreshEnabled()
			? {
					title: messages.actions,
					Cell: ActionsCell,
					width: 3.4,
					dataTestId: 'filters-main.content.headerCell.actions.container',
				}
			: undefined,
	].filter(Boolean),
);

export const getTableLabel = () => messages.tableAriaLabel;

export const getResultsAnnouncerMessage = () => messages.filterResultsAnnouncement;
